import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from '@digital-twins/design-system/icons'
import { AssetMediaRef } from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { MarkdownBody } from '@/components/ui/MarkdownBody'
import { cn } from '@/lib/utils'

import {
  firstLineOf,
  GateArtifactEntry,
  parseGateNoMatches,
  parseGateQuestions,
} from './gateArtifacts'
import { MarkdownHighlightTextarea } from './MarkdownHighlightTextarea'
import { RunGateNoMatchList } from './RunGateNoMatchList'
import { RunGateQuestionList } from './RunGateQuestionList'

/**
 * One artifact under review at a gate.
 *
 * TWO SHAPES, ONE CARD. Most artifacts are prose: a report, a brief. The
 * sequential composer review shows the body open (compact markdown, click to
 * edit). The watch-only list stays collapsed to a title and first line.
 *
 * Drafted questions are the other shape, and they behave differently on
 * purpose. A reviewer is being asked to approve spending twin credits on
 * questions, so the questions are shown, in full, without a click: the card
 * does not collapse, every prompt is written out, and each one carries the
 * scale it will be answered on and the follow-up as the twin will hear it.
 * Anything that does not parse as a questions payload falls back to the prose
 * rendering rather than failing, because a gate that cannot render is a gate
 * nobody can approve.
 *
 * EDITING. Questions use inline field edits (prompt / title / choices / scale
 * labels / follow-up) that stage into `GateEditState` via `onSaveEdit` — raw
 * JSON is not the default path. Prose still swaps the read view for a textarea
 * over the body. There is deliberately no per-field server save and no new
 * outward prop beyond staging.
 *
 * The card has no way to persist anything. `onSaveEdit` writes to the gate's
 * local draft state and nowhere else; the only outward call on this whole
 * surface is the gate's single approve payload.
 */

interface RunGateArtifactCardProps {
  artifact: GateArtifactEntry
  /** Position in the gate's flattened list. Drives every `data-testid` here. */
  position: number
  /** The body as it stands now: the local edit if there is one. */
  body: string
  isEdited: boolean
  isExpanded: boolean
  isEditing: boolean
  /**
   * Sequential review: body is always open. Watch-only list stays
   * collapsible so seven artifacts do not become seven walls of text.
   */
  forceOpen?: boolean
  /** False on a narrow viewport and on a non-editable gate. */
  canEdit: boolean
  onToggleExpand: () => void
  onStartEdit: () => void
  onSaveEdit: (body: string) => void
  /** Live-stage without closing. Sequential review types into this. */
  onStageEdit?: (body: string) => void
  onCancelEdit: () => void
  /**
   * Bound `run.inputs.stimulus` refs for the Versions list / thumbs. Empty
   * keeps today's artifact text versions.
   */
  stimulusRefs?: Array<AssetMediaRef>
  /** Peer-question ceiling, forwarded to `RunGateQuestionList`'s Add button. */
  maxQuestions?: number
  /** Study / thread content language for seeded option labels. */
  language?: string | null
}

export function RunGateArtifactCard(props: RunGateArtifactCardProps) {
  const {
    artifact,
    position,
    body,
    isEdited,
    isExpanded,
    isEditing,
    forceOpen = false,
    canEdit,
    onToggleExpand,
    onStartEdit,
    onSaveEdit,
    onStageEdit,
    onCancelEdit,
    stimulusRefs = [],
    maxQuestions,
    language = null,
  } = props

  const { t } = useTranslation('common')
  const { t: tApp } = useTranslation('app')

  const [draft, setDraft] = useState(body)
  const liveEdit = forceOpen && canEdit

  // Seed from the staged body. Sequential review is live, so a chat rewrite
  // that lands on this card must replace the textarea, not leave stale draft.
  useEffect(() => {
    if (isEditing || liveEdit) {
      setDraft(body)
    }
  }, [isEditing, liveEdit, artifact.key, body])

  const questions = useMemo(() => parseGateQuestions(body), [body])
  /* Read separately from the questions (C3). A payload can carry runnable
     questions, refused ones, or both, and a malformed caveat must never take
     the runnable list down with it. */
  const noMatches = useMemo(() => parseGateNoMatches(body), [body])

  const preview = firstLineOf(body)
  const showBody = forceOpen || isExpanded
  const titleClass = 'truncate text-xs font-bold text-foreground'

  const editedBadge = isEdited ? (
    <span
      className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground"
      data-testid={`run-gate-artifact-edited-${position}`}
    >
      {tApp('threads.detail.gate.edited')}
    </span>
  ) : null

  const editButton = canEdit ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs"
      onClick={onStartEdit}
      data-testid={`run-gate-edit-${position}`}
    >
      {t('edit')}
    </Button>
  ) : null

  const collapsibleHeader = (
    <button
      type="button"
      onClick={onToggleExpand}
      aria-expanded={isExpanded}
      className="flex w-full items-start gap-2 px-3 py-2.5 text-left"
      data-testid={`run-gate-artifact-toggle-${position}`}
    >
      <ChevronDown
        className={cn(
          'mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
          isExpanded && 'rotate-180',
        )}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className={titleClass}>{artifact.title}</span>
          {editedBadge}
        </span>
        {isExpanded ? null : (
          <span className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
            {preview}
          </span>
        )}
      </span>
    </button>
  )

  function handlePreviewActivate() {
    if (!canEdit) {
      return
    }
    onStartEdit()
  }

  const editor = (
    <div className="px-3 pb-3">
      <MarkdownHighlightTextarea
        value={draft}
        onChange={(event) => {
          const next = event.target.value
          setDraft(next)
          if (liveEdit && onStageEdit) {
            onStageEdit(next)
          }
        }}
        rows={14}
        aria-label={tApp('threads.detail.gate.editNamedAria', {
          name: artifact.title,
        })}
        className="min-h-[14rem]"
        data-testid={`run-gate-artifact-input-${position}`}
      />
      {liveEdit ? null : (
        <div className="mt-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={onCancelEdit}
            data-testid={`run-gate-artifact-cancel-${position}`}
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onSaveEdit(draft)}
            data-testid={`run-gate-artifact-save-${position}`}
          >
            {t('save')}
          </Button>
        </div>
      )}
    </div>
  )

  const reader = (
    <div className="px-3 pb-3">
      <div
        role={canEdit ? 'button' : undefined}
        tabIndex={canEdit ? 0 : undefined}
        onClick={canEdit ? handlePreviewActivate : undefined}
        onKeyDown={
          canEdit
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handlePreviewActivate()
                }
              }
            : undefined
        }
        className={cn(
          'rounded-md border border-input bg-background px-2.5 py-2',
          canEdit && 'cursor-text hover:border-foreground/25',
        )}
        data-testid={`run-gate-artifact-preview-${position}`}
      >
        <MarkdownBody content={body} density="compact" />
      </div>
      {canEdit ? <div className="mt-2 flex justify-end">{editButton}</div> : null}
    </div>
  )

  if (questions || noMatches) {
    return (
      <li
        className="space-y-2"
        data-testid={`run-gate-artifact-${position}`}
        data-edited={isEdited}
      >
        {editedBadge}
        {questions ? (
          <RunGateQuestionList
            questions={questions}
            position={position}
            isEditable={canEdit}
            body={body}
            onBodyChange={onSaveEdit}
            stimulusRefs={stimulusRefs}
            maxQuestions={maxQuestions}
            language={language}
          />
        ) : null}
        {noMatches ? (
          <RunGateNoMatchList
            entries={noMatches}
            position={position}
            maxQuestions={maxQuestions}
          />
        ) : null}
      </li>
    )
  }

  return (
    <li
      className="overflow-hidden rounded-lg border border-border bg-card"
      data-testid={`run-gate-artifact-${position}`}
      data-edited={isEdited}
    >
      {forceOpen ? (
        <div className="flex items-center gap-2 px-3 pb-1 pt-2">
          <span className={cn('min-w-0 flex-1', titleClass)}>{artifact.title}</span>
          {editedBadge}
        </div>
      ) : (
        collapsibleHeader
      )}
      {showBody ? (
        <div className={forceOpen ? undefined : 'border-t border-border/60'}>
          {liveEdit || isEditing ? editor : reader}
        </div>
      ) : null}
    </li>
  )
}
